"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { usePipelineStages } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import type { PipelineStage } from "@/app/(authenticated)/leads/_modules/types/pipeline-stage.types";
import { LeadStatus } from "@/app/_module/constants/leadStatus";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

/**
 * An agent's own disposition buttons on the dialers: pipelines (the Leads page columns —
 * pipeline_stages rows; agents call them folders) picked in the dialer, stored as stage ids
 * in user_dialer_preferences.quick_dispositions (button order). Ids are resolved against the
 * current agency's stages, so a renamed pipeline keeps its button and another agency's picks
 * just don't show here. Never set up (null) = the default pipelines every agent gets.
 */

interface QuickDispositionsBody {
  success?: boolean;
  error?: string;
  data?: { quickDispositions?: unknown };
}

// Already buttons on every dialer (and DNC asks about blocking), so never offered twice.
const BUILT_IN = new Set<string>([LeadStatus.BAD_NUMBER, LeadStatus.DO_NOT_CALL, LeadStatus.NO_SALE]);

// ⚠️ MIRRORED in the backend PipelineStageService (DEFAULT_CUSTOM_STAGES), which gives every
// pipeline these three — change both together. Matched by name so an agent's own "AUTO-VM" counts.
const DEFAULT_BUTTONS = ["autovm", "screenedcall", "novoicemail"];
const squash = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, "");
const ENDPOINT = "/dialer-preferences/quick-dispositions";

export const QUICK_DISPOSITIONS_KEY = ["dialer-preferences", "quick-dispositions"] as const;
export const MAX_QUICK_DISPOSITIONS = 9;

export interface QuickDisposition {
  id: string;
  name: string;
  color: string;
  /** Icon name from the stage (pipeline_stages.icon) — see pipelineStageIcon. */
  icon?: string;
  /** What the lead's status becomes — the same value the Leads page sets. */
  value: string;
}

// The stages endpoint only returns visible ones.
export const isPickableStage = (s: PipelineStage) => !BUILT_IN.has(s.systemValue ?? "");

export function useQuickDispositions() {
  const prefs = useQuery({
    queryKey: QUICK_DISPOSITIONS_KEY,
    queryFn: async () => {
      const res = await api.get<QuickDispositionsBody>({ url: getApiUrl(ENDPOINT), auth: true });
      const ids = res.data.data?.quickDispositions;
      return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : null;
    },
    staleTime: 5 * 60 * 1000,
  });
  const stages = usePipelineStages();
  const byId = new Map((stages.data ?? []).map((s) => [s.id, s] as const));
  const defaults = DEFAULT_BUTTONS.map((n) => stages.data?.find((s) => !s.isSystem && squash(s.name) === n)?.id);
  // undefined = still loading (or failed): nothing yet, so an agent who removed the defaults never
  // sees them flash. null = never set up: the defaults.
  const ids = prefs.data === undefined ? [] : (prefs.data ?? defaults.filter((id): id is string => !!id));
  const buttons: QuickDisposition[] = ids
    .map((id) => byId.get(id))
    .filter((s): s is PipelineStage => !!s && isPickableStage(s))
    .map((s) => ({ id: s.id, name: s.name, color: s.color, icon: s.icon, value: s.systemValue || s.name }));
  return {
    buttons,
    savedIds: prefs.data ?? [],
    /** Every stage id of the current agency — saved ids outside it belong to another agency. */
    agencyStageIds: new Set(byId.keys()),
    stages: (stages.data ?? []).filter(isPickableStage),
    // Saving before both load would overwrite the agent's list with a partial one.
    ready: prefs.isSuccess && stages.isSuccess,
  };
}

export function useSaveQuickDispositions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await api.put<QuickDispositionsBody>({
        url: getApiUrl(ENDPOINT),
        body: { quickDispositions: ids },
        auth: true,
      });
      // The controller answers validation errors with 200 + { success: false }.
      if (res.data.success === false) throw new Error(res.data.error || "Couldn't save your buttons");
      return ids;
    },
    onSuccess: (ids) => queryClient.setQueryData(QUICK_DISPOSITIONS_KEY, ids),
  });
}
